<script setup lang="ts">
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computedAsync, useElementVisibility } from '@vueuse/core';
import { computed, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { RouteLocationNormalized, useRoute } from 'vue-router';

import { getPredictedShippingCutoff } from '@/api/predictedShipping';
import Accordion from '@/components/base/layout/Accordion.vue';
import Breadcrumb from '@/components/base/layout/Breadcrumb.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import Header6 from '@/components/base/typography/Header6.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import CmsContent from '@/components/cms/CmsContent.vue';
import DeliveryEstimate from '@/components/pdp/buy-block/DeliveryEstimate.vue';
import GiftCheckbox from '@/components/pdp/buy-block/GiftCheckbox.vue';
import FavoriteButton from '@/components/pdp/FavoriteButton.vue';
import GalleryWrapper from '@/components/pdp/GalleryWrapper.vue';
import PDPMeta from '@/components/pdp/PDPMeta.vue';
import PDPSkeleton from '@/components/pdp/PDPSkeleton.vue';
import PDPTitle from '@/components/pdp/PDPTitle.vue';
import ProductDescription from '@/components/pdp/ProductDescription.vue';
import ProductMeta from '@/components/pdp/ProductMeta.vue';
import VariantOption from '@/components/ppa/VariantOption.vue';
import BundleDiscountBadge from '@/components/product-card/BundleDiscountBadge.vue';
import { useCms } from '@/composables/useCms';
import { CUSTOM_MIX_5LB_SKU, CUSTOM_MIX_25LB_SKU } from '@/lib/customizers/mixes';
import { LARGE_CUSTOM_TRAY_SKU, MEDIUM_CUSTOM_TRAY_SKU } from '@/lib/customizers/trays';
import { SupportedCustomProductKey } from '@/lib/product';
import { useCustomProductStore } from '@/stores/customProduct';
import { sendProductViewedEvent } from '@/utils/analytics/productViewedEvent';
import { NutsProduct, PurchaseOptions, toLegacySku } from '@/utils/product';

const VARIANT_OPTIONS: Record<string, { title: string; description: string }> = {
  [CUSTOM_MIX_5LB_SKU]: { title: 'Large Bag', description: '5 lbs' },
  [CUSTOM_MIX_25LB_SKU]: { title: 'Case', description: '25 lbs' },
  [MEDIUM_CUSTOM_TRAY_SKU]: { title: 'Medium Tray', description: 'Approx. 3.5 lbs' },
  [LARGE_CUSTOM_TRAY_SKU]: { title: 'Large Tray', description: 'Approx. 5.5 lbs' },
};

interface Props {
  customProductKey: SupportedCustomProductKey;
  isFavorited: boolean;
}

const props = defineProps<Props>();

const route = useRoute();

const customProductStore = ref(useCustomProductStore(props.customProductKey));
const products = computed(() => customProductStore.value.products);
const error = computed(() => customProductStore.value.error);
const isPending = computed(() => customProductStore.value.isPending);

const underTheFoldContent = useTemplateRef('underTheFoldContent');
const underTheFoldContentVisible = useElementVisibility(underTheFoldContent);
const { content: cmsContent, loadCmsContent } = useCms('custom-pdp-under-the-fold-content', {
  contentVisible: () => underTheFoldContentVisible.value,
  prefetch: 'setup',
});

const expandedTabs = reactive({ details: true });

const selections = reactive<PurchaseOptions>({
  markedAsGift: false,
  quantity: 1,
  sku: '',
  variation: 0,
});

const allProducts = computed(() => products.value.map(NutsProduct.fromNutsProductProjection));
const allVariants = computed(() => allProducts.value.flatMap((p) => p.variants));

const product = computed<NutsProduct | undefined>(() => allProducts.value[0]);
const isFavorited = ref(props.isFavorited);

const breadcrumbs = computed(() => [
  { name: 'Home', url: '/' },
  ...(product.value?.ancestors || []),
]);
const canDisplayProduct = computed(() => !error.value && !isPending.value && !!product.value);
const selectedVariant = computed(
  () => product.value?.variants.find((v) => v.sku === selections.sku) || product.value?.variants[0],
);
const titleImage = computed(() => product.value?.titleImage || undefined);
const predictedShipping = computedAsync(async () => {
  const shippingCutoff = await getPredictedShippingCutoff();
  return shippingCutoff;
});
const builderPath = computed(() => {
  const basePath = `${route.path.replace(/\/$/, '')}/builder`;
  const params = new URLSearchParams();

  switch (selections.sku) {
    case LARGE_CUSTOM_TRAY_SKU:
      params.set('code', 'l');
      break;
    case CUSTOM_MIX_5LB_SKU:
    case CUSTOM_MIX_25LB_SKU:
      params.set('sku', selections.sku);
      break;
    default:
      break;
  }

  if (selections.markedAsGift) {
    params.set('asGift', 'true');
  }

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
});

function normalizeTitle(title: string) {
  return title
    .replace(/\b(Medium|Large)\b/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

onMounted(() => {
  watch(
    () => props.customProductKey,
    async (customProductKey) => {
      loadCmsContent();

      const newCustomProductStore = useCustomProductStore(customProductKey);
      await newCustomProductStore.loadProducts();

      customProductStore.value = newCustomProductStore;
    },
    { immediate: true },
  );
  watch(
    [product, selectedVariant],
    () => {
      if (!product.value || !selectedVariant.value) {
        selections.sku = '';
        selections.variation = 0;
        return;
      }

      selections.sku = selectedVariant.value.sku;
      selections.variation = selectedVariant.value.id;

      sendProductViewedEvent({
        name: product.value.name,
        price: from(0),
        productId: product.value.key,
        sku: selectedVariant.value.sku,
        unitName: selectedVariant.value.unitName,
        weight: selectedVariant.value.weight,
      });
    },
    { immediate: true },
  );
});

defineOptions({
  async criticalData(_: any, to: RouteLocationNormalized) {
    if (typeof window === 'undefined') {
      const incomingProps = to.matched[0].props.default as Props;

      await useCustomProductStore(incomingProps.customProductKey).loadProducts();
    }
  },
});
</script>

<template>
  <PDPSkeleton v-if="!(canDisplayProduct && product && selectedVariant)" class="animate-pulse" />
  <div v-else class="flex flex-col">
    <PDPMeta :image="titleImage" :product :variant="selectedVariant" />
    <ProductMeta
      :prefixedSku="toLegacySku(selectedVariant.sku)"
      :product
      :variant="selectedVariant"
    />
    <div class="mx-auto w-full max-w-screen-2xl">
      <div
        class="flex flex-row gap-8 justify-between items-center pt-4 lg:pt-6 lg:flex-row lg:mx-10"
      >
        <Breadcrumb :ancestors="breadcrumbs" scrollPaddingClasses="px-4 lg:px-0" />
      </div>
      <div class="flex relative flex-col pt-3 pb-10 sm:pt-8 lg:flex-row lg:gap-8 lg:mx-10">
        <div class="px-4 lg:hidden lg:px-0">
          <BundleDiscountBadge :productKey="product.key" />
          <PDPTitle class="mt-2" data-test="product-title">
            {{ normalizeTitle(product.name) }}
          </PDPTitle>
        </div>
        <div
          class="relative block mx-auto mt-4 gallery lg:sticky lg:top-20 lg:mt-0 h-[317px] min-h-[317px]"
        >
          <GalleryWrapper
            v-if="selectedVariant"
            headerTag="h2"
            :images="product.images"
            :productName="product.name"
            :showBagSizeBadge="false"
            :showPackageSizeImage="false"
            :tagNames="[]"
            :titleImage
            :variant="selectedVariant"
          />
        </div>
        <div class="w-full shrink-0 lg:w-[432px] xl:w-[554px] px-4 lg:px-0">
          <div class="flex flex-col" data-test="buy-block">
            <div class="hidden lg:visible lg:inline">
              <BundleDiscountBadge :productKey="product.key" />
              <PDPTitle class="mt-2">{{ normalizeTitle(product.name) }}</PDPTitle>
            </div>
            <fieldset class="lg:mt-6">
              <legend class="items-center text-xs sm:text-sm" data-test="gift-value">
                <strong>Size:</strong>
                <span class="ml-1 text-neutral-600">
                  {{ VARIANT_OPTIONS[selectedVariant.sku].description }}
                </span>
                <span v-if="selectedVariant.backordered" class="ml-1 text-nuts-red-800">
                  Out of stock
                </span>
              </legend>
              <div class="grid grid-cols-3 gap-2">
                <VariantOption
                  v-for="(variant, index) in allVariants"
                  v-model="selections.sku"
                  class="mt-2 w-full lg:max-w-xs max-w-60"
                  :isPrimary="index === 0"
                  :isSelected="variant.sku === selections.sku"
                  :key="variant.sku"
                  :selections
                  :showSavings="false"
                  :variant
                >
                  <template #default="{ isSelected }">
                    <div
                      class="flex flex-col justify-center items-start w-full h-full"
                      :class="index === 0 ? 'pt-4' : 'pt-2'"
                    >
                      <Header6
                        :class="{ 'option-title': !isSelected }"
                        font="sofia-pro"
                        headerTag="p"
                      >
                        {{ VARIANT_OPTIONS[variant.sku].title }}
                      </Header6>
                      <SmallBodyText
                        class="font-normal"
                        :class="isSelected ? 'text-black' : 'text-neutral-500'"
                      >
                        {{ VARIANT_OPTIONS[variant.sku].description }}
                      </SmallBodyText>
                    </div>
                  </template>
                </VariantOption>
              </div>
            </fieldset>

            <DeliveryEstimate
              class="mt-4 lg:mt-6"
              hideTruckIcon
              multipleShipments
              :predictedShipping
              :quantity="selections.quantity"
              :sku="selectedVariant.sku"
              :totalPrice="from(0)"
              :weight="selectedVariant.weight"
            />

            <RouterLink class="flex mt-6 h-12 rounded-full link-button" :to="builderPath">
              <ThemedButton class="h-12" fullWidth tabindex="-1">
                <Header6 class="semibold" headerTag="p">Customize</Header6>
              </ThemedButton>
            </RouterLink>

            <FavoriteButton class="flex mt-6 w-fit" :isFavorited :product :selectedVariant />
            <GiftCheckbox v-model="selections.markedAsGift" class="flex mt-2.5" />

            <Accordion
              v-model:expanded="expandedTabs.details"
              class="mt-4 border-solid border-y border-neutral-200 md:mt-6"
              defaultExpanded
              headerTag="h2"
              id="product-description"
            >
              Product Details
              <template #content>
                <ProductDescription
                  class="mt-4 mb-2 md:mt-0"
                  :description="product.descriptionHtml"
                  :healthTips="product.healthTipsHtml"
                />
              </template>
            </Accordion>
          </div>
        </div>
      </div>
    </div>

    <div ref="underTheFoldContent">
      <CmsContent :content="cmsContent" model="custom-pdp-under-the-fold-content" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.gallery {
  @apply 2xl:h-[670px] min-[1440px]:h-[574px] xl:h-[470px] sm:h-[374px] w-full max-w-[867px] aspect-[4/3];
}
.link-button {
  @apply no-underline;
}
</style>
